// Flux du jour : contrat de revue événementiel v1.
// Le client échoue fermé si l'API ne fournit pas les références nécessaires
// à un traitement indépendant et optimiste de chaque événement.

import { clientAppFetch } from "./clientAppFetch";

export const SUIVI_REVIEW_CONTRACT_VERSION = 1;
const EVENT_TYPES = new Set(["feedback", "client_message", "meal_photo"]);

export class SuiviReviewContractError extends Error {
  constructor(message) {
    super(message);
    this.name = "SuiviReviewContractError";
  }
}

function assertEvent(event, field, index) {
  const prefix = `${field}[${index}]`;
  if (!event || typeof event !== "object" || Array.isArray(event)) {
    throw new SuiviReviewContractError(`${prefix} invalide dans le contrat de revue`);
  }
  if ((typeof event.id !== "string" && typeof event.id !== "number") || String(event.id).trim() === "") {
    throw new SuiviReviewContractError(`${prefix}.id manquant dans le contrat de revue`);
  }
  if (typeof event.source_version !== "string" || !event.source_version.trim() || !Number.isFinite(Date.parse(event.source_version))) {
    throw new SuiviReviewContractError(`${prefix}.source_version invalide`);
  }
  if (event.treated_at !== null && (
    typeof event.treated_at !== "string"
    || !event.treated_at.trim()
    || !Number.isFinite(Date.parse(event.treated_at))
  )) {
    throw new SuiviReviewContractError(`${prefix}.treated_at invalide`);
  }
}

/** Valide et normalise strictement la réponse du GET feedbacks-stream. */
export function validateFeedbacksStreamContract(body) {
  if (!body || body.review_contract_version !== SUIVI_REVIEW_CONTRACT_VERSION) {
    throw new SuiviReviewContractError("Contrat de revue du Flux du jour absent ou incompatible");
  }

  const clients = Array.isArray(body.clients) ? body.clients : [];
  const feedbacks = Array.isArray(body.feedbacks) ? body.feedbacks : [];
  const photos = Array.isArray(body.photos) ? body.photos : [];
  const clientMessages = Array.isArray(body.client_messages) ? body.client_messages : [];

  feedbacks.forEach((event, index) => assertEvent(event, "feedbacks", index));
  photos.forEach((event, index) => assertEvent(event, "photos", index));
  clientMessages.forEach((event, index) => assertEvent(event, "client_messages", index));

  return {
    review_contract_version: SUIVI_REVIEW_CONTRACT_VERSION,
    clients,
    feedbacks,
    photos,
    clientMessages,
  };
}

export function suiviEventKey(ref) {
  if (!ref || !EVENT_TYPES.has(ref.type)) {
    throw new SuiviReviewContractError("Type de référence Suivi invalide");
  }
  return JSON.stringify([ref.type, String(ref.id), ref.source_version]);
}

export function getSuiviEventRefs(item) {
  if (item?.type === "feedback") {
    return [{ type: "feedback", id: item.feedback.id, source_version: item.feedback.source_version }];
  }
  if (item?.type === "message") {
    return [{ type: "client_message", id: item.message.id, source_version: item.message.source_version }];
  }
  if (item?.type === "photos") {
    return item.group.photos.map((photo) => ({
      type: "meal_photo",
      id: photo.id,
      source_version: photo.source_version,
    }));
  }
  throw new SuiviReviewContractError("Élément du Flux du jour invalide");
}

export function suiviItemKey(item) {
  return getSuiviEventRefs(item).map(suiviEventKey).join("|");
}

export function stageOptimisticTreatments(previous, refs, operationId) {
  const next = { ...previous };
  for (const ref of refs) next[suiviEventKey(ref)] = operationId;
  return next;
}

export function rollbackOptimisticTreatments(previous, refs, operationId) {
  const next = { ...previous };
  for (const ref of refs) {
    const key = suiviEventKey(ref);
    if (next[key] === operationId) delete next[key];
  }
  return next;
}

function isEventTreated(type, event, optimisticKeys) {
  return Boolean(event.treated_at) || optimisticKeys.has(suiviEventKey({
    type,
    id: event.id,
    source_version: event.source_version,
  }));
}

/**
 * Classement pur de l'inbox. Un groupe de photos correspond au snapshot
 * exact affiché et n'est traité que si chacune de ses photos l'est.
 */
export function buildSuiviInbox(stream, optimisticKeys = new Set()) {
  const clientById = new Map(stream.clients.map((client) => [client.client_id, client]));
  const items = [];

  for (const feedback of stream.feedbacks) {
    const client = clientById.get(feedback.client_id);
    if (!client) continue;
    items.push({
      type: "feedback",
      at: new Date(feedback.created_at).getTime(),
      feedback,
      client,
      isTreated: isEventTreated("feedback", feedback, optimisticKeys),
    });
  }

  const photoGroups = new Map();
  for (const photo of stream.photos) {
    const client = clientById.get(photo.client_id);
    if (!client) continue;
    const key = JSON.stringify([photo.client_id, photo.taken_on]);
    const group = photoGroups.get(key) || { client, day: photo.taken_on, photos: [] };
    group.photos.push(photo);
    photoGroups.set(key, group);
  }
  for (const group of photoGroups.values()) {
    group.photos.sort((a, b) => {
      const byDate = new Date(a.created_at) - new Date(b.created_at);
      return byDate || String(a.id).localeCompare(String(b.id));
    });
    items.push({
      type: "photos",
      at: Math.max(...group.photos.map((photo) => new Date(photo.created_at).getTime())),
      group,
      client: group.client,
      isTreated: group.photos.every((photo) => isEventTreated("meal_photo", photo, optimisticKeys)),
    });
  }

  for (const message of stream.clientMessages) {
    const client = clientById.get(message.client_id);
    if (!client) continue;
    items.push({
      type: "message",
      at: new Date(message.sent_at).getTime(),
      message,
      client,
      isTreated: isEventTreated("client_message", message, optimisticKeys),
    });
  }

  items.sort((a, b) => b.at - a.at);
  return {
    toReply: items.filter((item) => !item.isTreated && item.type === "message"),
    information: items.filter((item) => !item.isTreated && item.type !== "message"),
    treated: items.filter((item) => item.isTreated),
  };
}

/** Charge le Flux du jour en exigeant le contrat événementiel v1. */
export async function fetchFeedbacksStream(days = 14, { fetchApi = clientAppFetch } = {}) {
  const body = await fetchApi("/api/admin/feedbacks-stream", {
    method: "GET",
    query: { days, review_contract_version: SUIVI_REVIEW_CONTRACT_VERSION },
  });
  return validateFeedbacksStreamContract(body);
}
